import { useCallback, useEffect, useState } from 'react';
import { GameFrame, pick, splitFace, useGame, type GameProps } from '../sdk/react';
import { KINDS } from '../../concepts/kinds';
import { Pic } from '../sdk/react';

type Row = { label: string; items: string[] };

const LEVELS = [
  { value: 'independent', label: 'Did it alone' },
  { value: 'gesture', label: 'Needed a point or gesture' },
  { value: 'model', label: 'Needed showing' },
  { value: 'physical', label: 'Needed hands-on help' },
] as const;

export default function TaskChain(props: GameProps) {
  const game = useGame(props);
  const rows = ((game.config?.sequences ?? []) as Row[]).filter((r) => r?.label && Array.isArray(r.items) && r.items.length > 0);
  const [row, setRow] = useState<Row | null>(null);
  const [step, setStep] = useState(0);
  const [trial, setTrial] = useState<string | null>(null);
  const [record, setRecord] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const openStep = useCallback(async (r: Row, i: number) => {
    const id = await game.register({ task: r.label, step: i + 1, text: r.items[i] }, { value: 'independent' });
    if (!id) return;
    setStep(i);
    setTrial(id);
  }, []);

  const start = useCallback(async () => {
    if (rows.length === 0) return;
    const next = pick(rows);
    setRow(next);
    setRecord([]);
    await openStep(next, 0);
  }, [rows.map((r) => r.label + r.items.join()).join('|'), openStep]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => {
    if (!row || game.textPrompt || step >= row.items.length) return;
    game.say(row.items[step]);
  }, [row, step]);

  async function score(level: string) {
    if (!row || !trial || busy) return;
    setBusy(true);
    const v = await game.respond(trial, level);
    setBusy(false);
    if (!v) return;
    const rec = [...record, level];
    setRecord(rec);
    if (step + 1 < row.items.length) await openStep(row, step + 1);
    else setStep(row.items.length);
  }

  const finished = row && step >= row.items.length;
  const current = row && !finished ? splitFace(row.items[step]) : null;

  return (
    <GameFrame game={game} empty={rows.length === 0} emptyText={`${KINDS.taskChain.describes} Edit it to add a task.`} onResume={() => { void start(); }} breakTrial={trial}>
      {row && (
        <div className="g-board g-chain">
          <h2 className="g-chain-title">{row.label}</h2>
          <ol className="g-chain-steps" aria-label="Steps">
            {row.items.map((_s, i) => (
              <li key={i} className={i < step ? 'is-done' : i === step ? 'is-now' : ''} aria-current={i === step ? 'step' : undefined}>
                <span className="g-chain-n">{i < step ? '✓' : i + 1}</span>
              </li>
            ))}
          </ol>
          {current ? (
            <>
              <div className="g-hero" aria-hidden="true"><Pic text={row.items[step]} fallback={current.picture} className="g-hero-img" /></div>
              <div className="g-prompt" aria-live="polite">
                <span className="g-prompt-text">{current.word}</span>
                {!game.textPrompt && <button type="button" className="g-say" aria-label="Say it again" onClick={() => game.say(row.items[step])}><span aria-hidden="true">🔊</span></button>}
              </div>
              <div className="g-levels" role="group" aria-label="How much help this step needed">
                <span className="field-label">For the adult — how much help did this step need?</span>
                {LEVELS.map((l) => (
                  <button key={l.value} type="button" className={`btn${l.value === 'independent' ? '' : ' secondary'}`} disabled={busy} onClick={() => void score(l.value)}>{l.label}</button>
                ))}
              </div>
            </>
          ) : (
            <div className="g-chain-summary">
              <p className="g-prompt-text">All done!</p>
              <ul>
                {row.items.map((s, i) => (
                  <li key={i}><span>{splitFace(s).word}</span> — <strong>{LEVELS.find((l) => l.value === record[i])?.label}</strong></li>
                ))}
              </ul>
              <button className="btn" onClick={() => void start()}>Start again</button>
            </div>
          )}
        </div>
      )}
    </GameFrame>
  );
}
